import { BlobReader, BlobWriter, TextReader, ZipWriter } from "@zip.js/zip.js";
import { convertArrayToCSV } from "convert-array-to-csv";
import writeXlsxFile from "write-excel-file/browser";
import JSONBig from "json-bigint";
import { MIME_TYPE_TO_EXTENSION } from "@hightouch/lib/util/mime-types";

/**
 * Fetches a URL and returns its content as a Blob.
 * Returns `null` if the fetch fails so callers can skip gracefully.
 */
export const fetchUrlAsBlob = async (url: string): Promise<Blob | null> => {
  try {
    const response = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!response.ok) return null;
    return await response.blob();
  } catch {
    return null;
  }
};

/**
 * Appends a file extension derived from a MIME type when the name doesn't
 * already have one. Falls back to `.png` when the MIME type is unknown.
 */
export const resolveFilename = (name: string, contentType: string): string => {
  if (/\.\w{2,5}$/.test(name)) return name;
  const ext = MIME_TYPE_TO_EXTENSION[contentType] ?? "png";
  return `${name}.${ext}`;
};

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/png;base64,")
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function blobToDataUri(blob: Blob): Promise<string> {
  return `data:${blob.type};base64,${await blobToBase64(blob)}`;
}

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 150);
  };

  a.addEventListener("click", clickHandler, false);

  a.click();
};

export const downloadJson = (data: any, filename: string) => {
  const blob = new Blob([JSONBig.stringify(data)], {
    type: "application/json",
  });

  downloadBlob(blob, filename);
};

export const downloadCsv = (data: any, filename: string) => {
  const csvString = typeof data === "string" ? data : convertArrayToCSV(data);
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

  downloadBlob(blob, filename);
};

export type XlsxSheet = {
  /** Worksheet tab name. Excel rejects names longer than 31 characters. */
  name: string;
  /** Rows of cell values, header row included. */
  rows: string[][];
};

export const downloadXlsx = async (sheets: XlsxSheet[], filename: string) => {
  const workbook = writeXlsxFile(
    sheets.map((sheet) => ({ sheet: sheet.name, data: sheet.rows })),
  );

  downloadBlob(await workbook.toBlob(), filename);
};

export const downloadText = (data: string, filename: string) => {
  const blob = new Blob([data], { type: "text/plain;charset=utf-8;" });

  downloadBlob(blob, filename);
};

/**
 * Replaces characters that are illegal in filenames on common operating
 * systems (path separators and the Windows-reserved set) with `-`, collapses
 * whitespace and dash runs, then trims separators from the ends. Preserves
 * single spaces and otherwise-legal symbols (e.g. the `×` in "1080×1920") so
 * names stay human-readable. Does not enforce uniqueness — pair it with
 * deduplicateFilenames when building an archive.
 */
export const sanitizeFilename = (value: string): string =>
  value
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-")
    .replace(/^[\s-]+|[\s-]+$/g, "");

/**
 * Disambiguates duplicate filenames by appending a numeric suffix
 * (`name-2.ext`, `name-3.ext`, …) while preserving the extension. A ZIP archive
 * cannot contain two entries with the same name — `ZipWriter.add` throws
 * "File already exists" — so any helper that builds an archive from
 * caller-supplied names must guarantee uniqueness first. Distinct inputs can
 * legitimately collapse to the same name (e.g. two channel sizes that share a
 * label), and unrelated callers can pass names that collide with a generated
 * suffix, so this tracks every emitted name and bumps the counter until free.
 */
export const deduplicateFilenames = <T extends { filename: string }>(
  items: T[],
): T[] => {
  const used = new Set<string>();
  return items.map((item) => {
    if (!used.has(item.filename)) {
      used.add(item.filename);
      return item;
    }
    const dotIndex = item.filename.lastIndexOf(".");
    const name =
      dotIndex > 0 ? item.filename.slice(0, dotIndex) : item.filename;
    const ext = dotIndex > 0 ? item.filename.slice(dotIndex) : "";
    let counter = 2;
    let candidate = `${name}-${counter}${ext}`;
    while (used.has(candidate)) {
      counter += 1;
      candidate = `${name}-${counter}${ext}`;
    }
    used.add(candidate);
    return { ...item, filename: candidate };
  });
};

export const downloadZip = async (
  data: Array<{ filename: string; data: string }>,
  filename: string,
) => {
  const zipWriter = new ZipWriter(new BlobWriter("application/zip"));
  await Promise.all(
    deduplicateFilenames(data).map(async (item) => {
      await zipWriter.add(item.filename, new TextReader(item.data));
    }),
  );
  const blob = await zipWriter.close();

  downloadBlob(blob, filename);
};

/**
 * Builds a ZIP archive from named blobs and returns it as a Blob, without
 * triggering a download — for callers that nest the archive inside another
 * artifact (e.g. per-creative HTML5 bundles inside a bulk download).
 */
export const createZipBlob = async (
  data: Array<{ filename: string; blob: Blob }>,
): Promise<Blob> => {
  const zipWriter = new ZipWriter(new BlobWriter("application/zip"));
  await Promise.all(
    deduplicateFilenames(data).map(async (item) => {
      await zipWriter.add(item.filename, new BlobReader(item.blob));
    }),
  );
  return zipWriter.close();
};

export const downloadBlobZip = async (
  data: Array<{ filename: string; blob: Blob }>,
  filename: string,
) => {
  downloadBlob(await createZipBlob(data), filename);
};

/**
 * Downloads a set of blobs as either a single raw file or a ZIP archive.
 * A single entry is downloaded directly under its own filename (never wrapped
 * in a ZIP — a one-image archive is a papercut users have to unzip for nothing);
 * two or more entries are bundled into `zipFilename`. Returns the number of
 * entries downloaded so callers can tailor their success copy.
 */
export const downloadBlobsAsZipOrSingle = async (
  data: Array<{ filename: string; blob: Blob }>,
  zipFilename: string,
): Promise<number> => {
  const [only] = data;
  if (only === undefined) return 0;
  if (data.length === 1) {
    downloadBlob(only.blob, only.filename);
    return 1;
  }
  await downloadBlobZip(data, zipFilename);
  return data.length;
};

/**
 * Hands the browser a remote URL to save under `filename`. Unlike
 * downloadBlob, the URL outlives the click, so there is nothing to revoke.
 */
export const downloadFromUrl = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
